import { Box } from '@mui/material';
import ProblemModal from '@acorn-portal/shared/src/components/modal/ProblemModal';
import { logErrorToSentry } from '@acorn-portal/shared/src/components/sentry/Integration';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { setStartDate } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { formatISO, parseISO } from 'date-fns';

interface IMakeAChangeJourneyWrapper {
	children: React.ReactNode;
}

const MakeAChangeJourneyWrapper = ({ children }: IMakeAChangeJourneyWrapper) => {
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const [isDateInPastError, setDateInPastError] = useState(false);
	const [newStartDateString, setNewStartDateString] = useState('');
	const dispatch = useAppDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const { handleDiscardMta } = useDiscardMta();

	function isInRoutes(routes: string[]): boolean {
		const currentRoute = location.pathname;
		return routes.includes(currentRoute);
	}

	function setStartDateChange(dateTime: Date) {
		const newDateTime = new Date(dateTime.getTime() + 15 * 60000);
		const iSOString = formatISO(newDateTime);
		dispatch(setStartDate(iSOString));
		const options: Intl.DateTimeFormatOptions = {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		};
		const dateString = newDateTime.toLocaleString('en-GB', options);
		setNewStartDateString(dateString);
	}

	useEffect(() => {
		if (!location.pathname.includes('make-a-change') && midTermAdjustment.hasInitialData) {
			handleDiscardMta(location.pathname)
				.catch((e) => {
					logErrorToSentry(e);
				})
				.finally(undefined);
		}
	}, [location]);

	useEffect(() => {
		const dateTimeString = midTermAdjustment.mtaStartDate;
		const dateTime = parseISO(dateTimeString);
		const interval = setInterval(() => {
			const now = new Date();
			if (
				midTermAdjustment.hasInitialData &&
				isInRoutes([
					ROUTE_PATHS.makeAChangeHome,
					ROUTE_PATHS.makeAChangeDrivers,
					ROUTE_PATHS.makeAChangeVehicle,
					ROUTE_PATHS.makeAChangeVehicle2,
					ROUTE_PATHS.makeAChangeAddress,
					ROUTE_PATHS.makeAChangeUsageAndMileage,
					ROUTE_PATHS.makeAChangeMileage,
					ROUTE_PATHS.makeAChangeUsageTaxi,
					ROUTE_PATHS.topUpMiles,
					ROUTE_PATHS.topUpMilesDate,
					ROUTE_PATHS.topUpMilesPrice,
				]) &&
				now > dateTime
			) {
				setStartDateChange(dateTime);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [midTermAdjustment]);

	return (
		<Box>
			{children}
			<ProblemModal
				open={isDateInPastError}
				title='Adjustment Date Error'
				onSubmit={() => navigate(ROUTE_PATHS.makeAChangeHome)}
				submitText='Change Start Time'
				description={`<p>Sorry, we cannot backdate cover to a date in the past! \n Your new start date and time has been set to <b>${newStartDateString}</b>.</p> <p>If you wish to set to another start time, please select "Change Start Time" below.</p>`}
				onClose={() => setDateInPastError(false)}
			/>
		</Box>
	);
};

export default MakeAChangeJourneyWrapper;
